import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies, max-lines -- Login page orchestrates multiple auth flows (magic link, password, forgot password, confirmations) and composes desktop/mobile layouts */
import { Box, Button, Link } from "@mui/material";

import { ThemeProvider } from "@mui/material/styles";
import { ensureCognitoUser } from "@src/appV2/Auth/cognito/cognitoAuth";
import { verifyEmailOtp } from "@src/appV2/Auth/cognito/emailOtpAuth";
import { requestPasswordReset } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { clearMagicLinkEmail } from "@src/appV2/Auth/cognito/magicLinkEmailStore";
import { initCognitoSession } from "@src/appV2/Auth/cognito/initCognitoSession";
import { isSafeRedirectUrl } from "@src/appV2/Auth/cognito/isSafeRedirectUrl";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { isPasskeySupported, signInWithPasskey } from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { getRememberedEmail } from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { requestRememberEmail } from "@src/appV2/Auth/rememberEmail/requestRememberEmail";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  logPasswordLoginFailure,
  logPasswordResetRequestFailure,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { MOBILE_SSO_CODE_VERIFIER_KEY } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, APP_V3_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { OutageBanner } from "@src/appV2/OutageBanner/OutageBanner";
import { useOutageBanner } from "@src/appV2/OutageBanner/useOutageBanner";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { loginWithEmailAndPassword } from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendEmailOtp } from "@src/mobile/Login/sendEmailOtp";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { type ReactElement, useCallback, useEffect, useState } from "react";

// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk after password login
import { useDispatch } from "react-redux";

import { useHistory, useLocation } from "react-router-dom";
import loginIllustration from "./assets/login-illustration.webp";
import { DesktopLoginLayout } from "./DesktopLoginLayout";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";
import { MobileLoginLayout } from "./MobileLoginLayout";
import { OtpEntryForm } from "./OtpEntryForm";
import { useResendCooldown } from "./useResendCooldown";
import { useSsoLogin } from "./useSsoLogin";
const WORKPLACE_SIGNUP_URL = "https://www.clipboardworks.com/workplaces/signup";
type LoginMode = "magicLink" | "password" | "forgotPassword" | "resetEmailSent";

const VALID_LOGIN_MODES: readonly LoginMode[] = [
  "magicLink",
  "password",
  "forgotPassword",
  "resetEmailSent",
];

function parseInitialLoginMode(search: string): LoginMode {
  const mode = new URLSearchParams(search).get("mode");
  return VALID_LOGIN_MODES.find((m) => m === mode) ?? "magicLink";
}

/**
 * Clears stale OAuth/PKCE state from previous failed SSO attempts so the
 * login page starts clean. Only clears OAuth flow state — not Cognito
 * tokens or session data, which are managed by the logout flow.
 */
function clearStaleAuthState(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState") || key?.includes("oauthPKCE")) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
}

export function Login(): ReactElement {
  const { tu } = useTu("login");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const history = useHistory();
  const location = useLocation();
  // Pre-fill the email remembered on this device, if the user opted in before.
  const [email, setEmail] = useState(() => getRememberedEmail() ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Whether the passwordless flow has advanced to its second step: the OTP
  // code-entry form when the email-OTP flag is on, or the "magic link sent"
  // confirmation when it's off.
  const [passwordlessRequested, setPasswordlessRequested] = useState(false);

  const [loginMode, setLoginMode] = useState<LoginMode>(() =>
    parseInitialLoginMode(location.search)
  );

  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetError, setResetError] = useState<string | undefined>();
  const [passwordlessError, setPasswordlessError] = useState<string | undefined>();
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | undefined>();

  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });
  // When on, the passwordless flow sends a 6-digit email OTP and prompts for the
  // code; when off (default), it falls back to the original magic-link email.
  const isEmailOtpEnabled = useCbhFlag(CbhFeatureFlag.EMAIL_OTP_INSTEAD_OF_MAGIC_LINK, {
    defaultValue: false,
  });
  const passkeySupported = isPasskeySupported();

  // Clear stale auth state when the login page mounts. Previous failed
  // SSO attempts or stale Cognito tokens can leave state that causes
  // infinite loading loops or redirect errors on retry.
  useEffect(() => {
    clearStaleAuthState();
  }, []);

  const { ssoMode, handleSsoRedirect } = useSsoLogin(email);
  const { secondsRemaining, isCoolingDown, startCooldown } = useResendCooldown();

  // Requests an email OTP for the current email and starts the resend cooldown
  // on success. Shared by the initial submit and the code-entry resend; returns
  // whether the request succeeded so callers can switch to the code-entry view.
  const sendOtp = useCallback(
    async (options?: { isResend?: boolean }) => {
      setLoading(true);
      setPasswordlessError(undefined);

      const result = await sendEmailOtp({
        email,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526).
        skipEnsureUser: options?.isResend ?? false,
      });

      if (result.success) {
        startCooldown();
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [email, startCooldown]
  );

  // Sends the magic link to the current email and starts the resend cooldown on
  // success. Shared by the initial submit and the confirmation-screen resend;
  // returns whether the send succeeded so callers can react (e.g. show the
  // confirmation screen).
  const sendMagicLink = useCallback(
    async (options?: { isResend?: boolean }) => {
      setLoading(true);
      setPasswordlessError(undefined);

      const result = await sendLoginEmail({
        email,
        requestedURL: localStorage.getItem("redirectURL") ?? undefined,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526).
        skipEnsureUser: options?.isResend ?? false,
      });

      if (result.success) {
        startCooldown();
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [email, startCooldown]
  );

  const handlePasswordlessSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    setPasskeyError(undefined);

    // Production: SSO replaces the email flow for CBH employees
    if (ssoMode === "sso_only") {
      setLoading(true);
      setPasswordlessError(undefined);
      await handleSsoRedirect("submit");
      setLoading(false);
      return;
    }

    const requested = isEmailOtpEnabled ? await sendOtp() : await sendMagicLink();
    if (requested) {
      setPasswordlessRequested(true);
    }
  }, [email, ssoMode, handleSsoRedirect, isEmailOtpEnabled, sendOtp, sendMagicLink]);

  // Resends the magic link to the same email from the confirmation screen.
  // Gated by a cooldown so users can't spam the endpoint; the cooldown
  // restarts on every successful resend. Magic-link flow only.
  const handleResendEmail = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendMagicLink({ isResend: true });
  }, [email, isCoolingDown, sendMagicLink]);

  // Resends the email OTP to the same address from the code-entry screen. Gated
  // by the same cooldown as the magic link resend so users can't spam the
  // endpoint; the cooldown restarts on every successful resend. Email-OTP flow
  // only.
  const handleResendOtp = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendOtp({ isResend: true });
  }, [email, isCoolingDown, sendOtp]);

  // Verifies the code typed on this (the requesting) browser. A single wrong
  // code fails the Cognito flow — the user can re-enter the code, resend a new
  // one, or go back.
  const handleVerifyOtp = useCallback(
    async (code: string) => {
      setLoading(true);
      setPasswordlessError(undefined);

      try {
        await verifyEmailOtp(code);

        // Request the post-sign-in "remember your email?" prompt (non-blocking).
        requestRememberEmail(email);

        const sessionRestored = await dispatch(initCognitoSession());
        if (!sessionRestored) {
          // The code verified but session hydration failed — a genuine defect,
          // not user friction. Record it as an unexpected error so it surfaces
          // in Error Tracking, mirroring the password flow's session-init path.
          logPasswordLoginFailure({
            eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
            error: new Error("Cognito session not restored after email OTP verification"),
            metadata: {
              outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
              reason: LOGIN_REASON.SESSION_HYDRATION_FAILED,
            },
          });
          setPasswordlessError(tu("Something went wrong. Please try again."));
          setLoading(false);
          return;
        }

        logEvent(APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });

        clearMagicLinkEmail();
        const storedRedirectUrl = localStorage.getItem("redirectURL");
        const redirectUrl =
          storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
        localStorage.removeItem("redirectURL");
        history.replace(redirectUrl);
      } catch (error: unknown) {
        const mapped = mapCognitoError(error);
        // Classify so expected friction (wrong/expired code) is recorded as a
        // Datadog action while genuine defects keep the error attached and
        // reach Error Tracking, matching the password-login pattern.
        const { outcome, reason } =
          mapped.code === "auth/unknown" && isNetworkFailure(error)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : classifyCognitoError(mapped.code);
        logPasswordLoginFailure({
          eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
          error,
          metadata: { errorCode: mapped.code, errorMessage: mapped.message, outcome, reason },
        });
        // A wrong/expired native EMAIL_OTP surfaces as CodeMismatchException
        // (auth/invalid-code) or ExpiredCodeException (auth/expired-code); an
        // expired sign-in session (NotAuthorizedException "session is expired")
        // also maps to auth/expired-code. Network/unknown failures map to
        // auth/unknown. Show the OTP-specific retry copy for all of them so users
        // don't get magic-link/reset guidance.
        const isOtpRetryable =
          mapped.code === "auth/unknown" ||
          mapped.code === "auth/invalid-code" ||
          mapped.code === "auth/expired-code";
        setPasswordlessError(
          isOtpRetryable
            ? tu("That code is incorrect or expired. Go back and request a new code.")
            : mapped.message
        );
        setLoading(false);
      }
    },
    [email, dispatch, history, tu]
  );

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);
    setLoginError(undefined);

    const result = await loginWithEmailAndPassword({ email, password });

    if (result.success) {
      // Request the post-sign-in "remember your email?" prompt (non-blocking;
      // the root-level manager shows it after we redirect).
      requestRememberEmail(email);

      // Hydrate Redux session so PrivateRoute sees a valid session immediately,
      // matching what AuthRedirect does for the magic link flow.
      const sessionRestored = await dispatch(initCognitoSession());
      if (!sessionRestored) {
        setLoginError(tu("Something went wrong. Please try again."));
        setLoading(false);
        return;
      }

      const storedRedirectUrl = localStorage.getItem("redirectURL");
      const redirectUrl =
        storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
      localStorage.removeItem("redirectURL");
      history.replace(redirectUrl);
      return;
    }

    setLoginError(result.error);
    setLoading(false);
  }, [email, password, dispatch, history, tu]);

  // Reaches `signInWithPasskey` synchronously from the click (no prior await)
  // so the WebAuthn ceremony stays inside the user gesture.
  const handlePasskeyClick = useCallback(async () => {
    if (!email) {
      return;
    }

    setPasskeyLoading(true);
    setPasskeyError(undefined);
    setPasswordlessError(undefined);
    setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
    logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, { hasEmail: Boolean(email) });

    // Cognito's user-existence-error prevention fabricates a challenge for
    // unknown emails (sometimes factor selection → "no-passkey", sometimes a
    // real WEB_AUTHN ceremony, sometimes a NotAuthorized/UserNotFound error), so
    // the client genuinely can't tell "no account", "account without a passkey",
    // and "wrong passkey" apart. Show one honest message for every passkey
    // failure instead of leaking a misleading user-existence claim.
    const passkeyUnavailableMessage = tu(
      "We couldn't sign you in with a passkey for this email. If you have an account, sign in with a link or password, then add a passkey from the My Account page."
    );

    try {
      const result = await signInWithPasskey(email.trim());

      if (result.status === "no-passkey") {
        logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL);
        setPasskeyError(passkeyUnavailableMessage);
        setPasskeyLoading(false);
        return;
      }

      // Request the post-sign-in "remember your email?" prompt (non-blocking).
      requestRememberEmail(email);

      const sessionRestored = await dispatch(initCognitoSession({ authMethod: "passkey" }));
      if (!sessionRestored) {
        setPasskeyError(tu("Something went wrong. Please try again."));
        setPasskeyLoading(false);
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
      const storedRedirectUrl = localStorage.getItem("redirectURL");
      const redirectUrl =
        storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
      localStorage.removeItem("redirectURL");
      history.replace(redirectUrl);
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "passkey");
      logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      // A user-not-found error here is just prevention noise — the email may
      // well be valid. Collapse it into the neutral message; keep genuine
      // passkey errors (cancelled, security, unsupported) actionable.
      setPasskeyError(
        mapped.code === "auth/user-not-found" ? passkeyUnavailableMessage : mapped.message
      );
      setPasskeyLoading(false);
    }
  }, [email, dispatch, history, tu]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) {
      setResetError(tu("Please enter your email address first."));
      return;
    }

    setLoading(true);
    setResetError(undefined);

    try {
      await ensureCognitoUser(email);
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      setLoginMode("resetEmailSent");
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "password");
      logPasswordResetRequestFailure({ error, metadata: { errorCode: mapped.code } });
      setResetError(mapped.message);
    } finally {
      setLoading(false);
    }
  }, [email, tu]);

  // Clear any stale passkey error when the email changes, so a "no passkey for
  // this email" message doesn't linger against a different address.
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setPasskeyError(undefined);
  }, []);

  const isDesktop = useIsDesktop();
  const outageBanner = useOutageBanner();
  const banner = outageBanner.isVisible ? <OutageBanner /> : undefined;

  const showIllustration =
    !(loginMode === "magicLink" && passwordlessRequested) && loginMode !== "resetEmailSent";

  const showWelcomeHeading =
    (loginMode === "magicLink" && !passwordlessRequested) || loginMode === "password";

  const formBox = (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {loginMode === "magicLink" && !passwordlessRequested && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          showSsoButton={ssoMode === "sso_and_magic_link"}
          errorMessage={passwordlessError}
          showPasskeyButton={isPasskeyAuthEnabled && passkeySupported && ssoMode !== "sso_only"}
          passkeyLoading={passkeyLoading}
          passkeyError={passkeyError}
          setEmail={handleEmailChange}
          onSsoClick={() => handleSsoRedirect("sso_button")}
          onSubmit={handlePasswordlessSubmit}
          onPasskeyClick={() => {
            void handlePasskeyClick();
          }}
          onPasskeyInvalidEmail={() => {
            setPasskeyError(tu("Please enter a valid email address to sign in with a passkey."));
          }}
          onSwitchToPassword={() => {
            setPasswordlessError(undefined);
            setPasskeyError(undefined);
            setLoginMode("password");
          }}
        />
      )}

      {loginMode === "magicLink" && passwordlessRequested && isEmailOtpEnabled && (
        <OtpEntryForm
          email={email}
          loading={loading}
          errorMessage={passwordlessError}
          isResendCoolingDown={isCoolingDown}
          resendSecondsRemaining={secondsRemaining}
          onSubmit={handleVerifyOtp}
          onResend={() => {
            void handleResendOtp();
          }}
          onBack={() => {
            setPasswordlessError(undefined);
            setPasswordlessRequested(false);
          }}
        />
      )}

      {loginMode === "magicLink" && passwordlessRequested && !isEmailOtpEnabled && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Verification link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("We have sent an email with a login link to {{email}}.", {
              values: { email },
            })}
          </Text>
          {passwordlessError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {passwordlessError}
            </Text>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              width: "100%",
            }}
          >
            <LoadingButton
              fullWidth
              variant="primary"
              size="large"
              isLoading={loading}
              disabled={isCoolingDown}
              onClick={() => {
                void handleResendEmail();
              }}
            >
              {isCoolingDown
                ? tu("Resend email in {{seconds}}s", { values: { seconds: secondsRemaining } })
                : tu("Resend email")}
            </LoadingButton>
            <Button
              variant="text"
              disabled={loading}
              sx={{ textTransform: "none", color: "primary.main" }}
              onClick={() => {
                setPasswordlessError(undefined);
                setPasswordlessRequested(false);
              }}
            >
              {tu("Go back to login page")}
            </Button>
          </Box>
        </Box>
      )}

      {loginMode === "password" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <LoginWithEmailAndPasswordForm
            email={email}
            password={password}
            loading={loading}
            errorMessage={loginError}
            setEmail={setEmail}
            setPassword={setPassword}
            onSubmit={handlePasswordSubmit}
            onForgotPassword={() => {
              setLoginError(undefined);
              setLoginMode("forgotPassword");
            }}
          />
          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              alignSelf: "stretch",
              gap: 5,
              marginTop: 4,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
            <Text variant="body2" sx={{ color: "base.inactive" }}>
              {tu("or")}
            </Text>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
          </Box>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              marginTop: 4,
              alignSelf: "center",
            }}
            onClick={() => {
              setLoginMode("magicLink");
            }}
          >
            {tu("Sign in with link")}
          </Button>
        </Box>
      )}

      {loginMode === "forgotPassword" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 4,
            width: "100%",
          }}
        >
          <Text bold variant="body1" sx={{ textAlign: "center" }}>
            {tu("Set/reset password")}
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("Enter your email address and we'll send you a link to set/reset your password.")}
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              {tu("Email")}
            </Text>
            <TextInput
              fullWidth
              required
              type="email"
              placeholder="your.email@example.com"
              value={email}
              disabled={loading}
              autoComplete="email"
              inputProps={{ "aria-label": "Email" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Box>
          {resetError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {resetError}
            </Text>
          )}
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={loading}
            disabled={!email}
            onClick={() => {
              void handleForgotPassword();
            }}
          >
            {tu("Send Set/Reset Link")}
          </LoadingButton>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              alignSelf: "center",
            }}
            onClick={() => {
              setResetError(undefined);
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {loginMode === "resetEmailSent" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Reset link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu(
              "We have sent a password reset link to {{email}}. Check your email and follow the instructions.",
              { values: { email } }
            )}
          </Text>
          <Button
            variant="text"
            sx={{ textTransform: "none", color: "primary.main" }}
            onClick={() => {
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {showIllustration && (
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Text variant="body2" sx={{ color: "base.muted" }}>
            {tu("Don't have a workplace account?")}
          </Text>
          <Link
            href={WORKPLACE_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: "0.875rem", color: "primary.main" }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.LOGIN_SIGNUP_LINK_CLICKED);
            }}
          >
            {tu("Sign up")}
          </Link>
        </Box>
      )}
    </Box>
  );

  if (isDesktop) {
    return (
      <ThemeProvider theme={berlinRebrandTheme}>
        <DesktopLoginLayout banner={banner}>
          {showIllustration && (
            <img
              src={loginIllustration}
              alt="Person sitting at a desk"
              style={{ height: "min(212px, 22vh)", width: "auto" }}
            />
          )}
          {showWelcomeHeading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Text
                variant="h5"
                sx={{
                  fontSize: "2rem",
                  lineHeight: 1.2,
                  color: "base.base",
                  textAlign: "center",
                }}
              >
                {tu("Welcome back")}
              </Text>
            </Box>
          )}
          {formBox}
        </DesktopLoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <MobileLoginLayout banner={banner}>
        {showIllustration && (
          <img
            src={loginIllustration}
            alt="Person sitting at a desk"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}
        {formBox}
      </MobileLoginLayout>
    </ThemeProvider>
  );
}
/* eslint-enable import/max-dependencies, max-lines */
